import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { api } from '../api/client';
import type {
  EntriesResponse,
  EntryDto,
  Puzzle,
  RevealStatus,
} from '../api/types';

export interface DayState {
  puzzle: Puzzle | null;
  myEntry: EntryDto | null;
  revealed: boolean;
  reveal: RevealStatus | null;
  /** Alle Einträge, die der Server für den Anfragenden freigibt:
   *  sobald er selbst getippt hat, sind die Einträge der anderen
   *  bereits enthalten — ohne auf Reveal zu warten. */
  allEntries: EntryDto[];
  loading: boolean;
  error: string | null;
}

const EMPTY_STATE: DayState = {
  puzzle: null,
  myEntry: null,
  revealed: false,
  reveal: null,
  allEntries: [],
  loading: true,
  error: null,
};

/**
 * Lädt das Rätsel für einen bestimmten Tag (YYYY-MM-DD) inklusive
 * eigenem Eintrag und Reveal-Status. `date === todayIso()` lädt das
 * laufende Rätsel; jeder andere Wert lädt ein historisches.
 */
export function useDay(date: string, playerId: number | null) {
  const [state, setState] = useState<DayState>(EMPTY_STATE);

  const refresh = useCallback(async (): Promise<void> => {
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      const { data: puzzle } = await api.get<Puzzle>(`/puzzle/by-date/${date}`);
      // Datum mitgeben: Pastpuzzle wiederholt Nummern, daher muss die
      // Auflösung von Einträgen/Reveal auf den richtigen Spieltag scopen.
      const q = `?date=${date}`;
      const [entriesRes, revealRes] = await Promise.all([
        api.get<EntriesResponse>(`/entries/${puzzle.puzzle_number}${q}`),
        api.get<RevealStatus>(`/reveal/${puzzle.puzzle_number}${q}`),
      ]);

      const myEntry = playerId == null
        ? null
        : entriesRes.data.entries.find((e) => e.player_id === playerId) ?? null;

      setState({
        puzzle,
        myEntry,
        revealed: entriesRes.data.revealed,
        reveal: revealRes.data,
        allEntries: entriesRes.data.entries,
        loading: false,
        error: null,
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setState({
          ...EMPTY_STATE,
          loading: false,
          error: 'An diesem Tag wurde noch kein Rätsel angelegt.',
        });
        return;
      }
      setState((s) => ({
        ...s,
        loading: false,
        error: 'Daten konnten nicht geladen werden',
      }));
    }
  }, [date, playerId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { ...state, refresh };
}
